// packages
import { useState } from 'react';
import cx from 'classnames';

// local
import FormField from '../shared/formfield.jsx';
import SelectFormField from '../shared/selectformfield.jsx';
import NextButton from './nextbutton.jsx';
import { useCountries, useSubdivisions } from './hooks.js';
import { createValidator, required, isEmail } from '../shared/validators.js';

const validator = createValidator({
  firstName: [required],
  lastName: [required],
  email: [required, isEmail],
  zip: [required],
});

const shippingValidator = createValidator({
  firstName: [required],
  lastName: [required],
  email: [required, isEmail],
  address1: [required],
  city: [required],
  state: [required],
  country: [required],
  zip: [required],
});

// some countries do not have states/provinces/regions
const noSubdivisionsShippingValidator = createValidator({
  firstName: [required],
  lastName: [required],
  email: [required, isEmail],
  address1: [required],
  city: [required],
  country: [required],
  zip: [required],
});

/**
 * @param {object} props
 * @param {boolean} props.active
 * @param {boolean} props.complete
 * @param {Record<string, string>} props.contactInfo
 * @param {(values: object) => void} props.onComplete
 * @param {boolean} props.receivingBenefit
 */
export default function ContactInfoForm({
  active,
  complete,
  contactInfo,
  onComplete,
  receivingBenefit,
}) {
  const [values, setValues] = useState(contactInfo);
  const [errors, setErrors] = useState({});

  const countries = useCountries();
  const subdivisions = useSubdivisions(values.country);
  const noSubdivisions = subdivisions.length === 0;

  /**
   * @param {React.ChangeEvent} event
   */
  function handleChange(event) {
    const { name, value } = event.target;

    const newValues = Object.assign({}, values, {
      [name]: value,
    });

    // we need to un-set "state" if "country" has changed
    if (values.country !== newValues.country) {
      newValues.state = '';
    }

    setValues(newValues);
  }

  // Returns an object of { fieldName: 'Error message' } or false if valid.
  function validate(values) {
    let errors;

    if (receivingBenefit) {
      if (noSubdivisions) {
        errors = noSubdivisionsShippingValidator(values);
      } else {
        errors = shippingValidator(values);
      }
    } else {
      errors = validator(values);
    }

    setErrors(errors);

    return errors;
  }

  /**
   *
   * @param {React.FormEvent} event
   */
  function handleNext(event) {
    event.preventDefault();

    const errors = validate(values);

    if (Object.keys(errors).length === 0) {
      onComplete(values);
    }
  }

  const className = cx('contact-info-form', {
    'shipping-benefits': receivingBenefit,
    'support-section-complete': !active && complete,
  });

  return (
    <div className={className}>
      <h2>Contact Info</h2>
      {active && (
        <div>
          <form
            id="contact-info-form"
            className="contact-info-form clearfix"
            onSubmit={handleNext}
          >
            <FormField
              onChange={handleChange}
              className="col6"
              name="firstName"
              value={values.firstName}
              errors={errors}
              label="First Name"
            />
            <FormField
              onChange={handleChange}
              className="col6 omega"
              name="lastName"
              value={values.lastName}
              errors={errors}
              label="Last Name"
            />
            <FormField
              onChange={handleChange}
              className="col6"
              name="email"
              type="email"
              value={values.email}
              errors={errors}
              label="Email"
            />
            <FormField
              onChange={handleChange}
              className="col6 omega"
              name="phone"
              value={values.phone}
              errors={errors}
              label="Phone #"
            />
            <FormField
              onChange={handleChange}
              className="col6"
              name="address1"
              value={values.address1}
              errors={errors}
              label="Address"
            />
            <FormField
              onChange={handleChange}
              className="col6 omega"
              name="address2"
              value={values.address2}
              errors={errors}
              label="Address 2"
            />
            <SelectFormField
              onChange={handleChange}
              className="col6"
              name="country"
              value={values.country}
              options={countries}
              errors={errors}
              label="Country"
            />
            <SelectFormField
              onChange={handleChange}
              className="col6 omega"
              name="state"
              value={values.state}
              options={subdivisions}
              errors={errors}
              label="State"
              disabled={noSubdivisions}
            />
            <FormField
              onChange={handleChange}
              className="col6"
              name="city"
              value={values.city}
              errors={errors}
              label="City"
            />
            <FormField
              onChange={handleChange}
              className="col6 omega"
              name="zip"
              value={values.zip}
              errors={errors}
              label="Zip/Postal Code"
            />
          </form>
          <NextButton
            onClick={handleNext}
            type="submit"
            form="contact-info-form"
            text={complete ? 'Update' : 'Next'}
          />
        </div>
      )}
      {!active && complete && <ContactFormComplete contactInfo={contactInfo} />}
    </div>
  );
}

/**
 *
 * @param {object} props
 * @param {Record<string, string>} props.contactInfo
 */
function ContactFormComplete({ contactInfo: savedInfo }) {
  return (
    <div>
      <div>{compactJoin([savedInfo.firstName, savedInfo.lastName], ' ')}</div>
      <div>{compactJoin([savedInfo.email, savedInfo.phone], ' · ')}</div>
      <div>{compactJoin([savedInfo.address1, savedInfo.address2], ', ')}</div>
      <div>
        {compactJoin([savedInfo.city, savedInfo.state, savedInfo.zip], ', ')}
      </div>
      <div>{savedInfo.country}</div>
    </div>
  );
}

function compactJoin(list, separator) {
  const compactList = list.filter((x) => !!x);
  return compactList.join(separator);
}
